import { useState } from "react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

/**
 * F-chantier-entity E1 — cadastro de CHANTIERS (locais de trabalho que reúnem
 * vários clientes e técnicos; modelo eDSP: o cliente é vinculado a um
 * chantier no cadastro e o carro herda pelo devis). Agenda vem na E2.
 */

const inputClass =
  "w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime";

type ChantierRow = {
  id: number;
  name: string;
  city: string | null;
  address: string | null;
  postalCode: string | null;
  notes: string | null;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
  customerCount: number;
};

export default function Chantiers() {
  const t = useT();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const listQuery = trpc.chantiers.list.useQuery(
    { companyId: companyId ?? 0, activeOnly: false },
    { enabled: companyId !== null },
  );
  const [editing, setEditing] = useState<ChantierRow | "new" | null>(null);

  if (!user) return null;

  return (
    <ErpShell>
      <main className="max-w-5xl mx-auto p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-wide">{t("chantiers.title")}</h1>
            <p className="text-sm text-neutral-500">{t("chantiers.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={companyId ?? ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className={inputClass + " w-auto"}
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {canEdit && (
              <button
                onClick={() => setEditing("new")}
                className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime"
              >
                + {t("chantiers.newBtn")}
              </button>
            )}
          </div>
        </div>

        {listQuery.isLoading && <p className="text-sm text-neutral-500">…</p>}
        {listQuery.data && listQuery.data.rows.length === 0 && (
          <p className="rounded-md border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-500">
            {t("chantiers.empty")}
          </p>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          {(listQuery.data?.rows ?? []).map((ch) => (
            <div
              key={ch.id}
              className={`rounded-lg border border-neutral-800 bg-neutral-900 p-4 ${ch.active ? "" : "opacity-60"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Link
                    href={`/chantiers/${ch.id}`}
                    className="font-semibold text-lime hover:underline"
                  >
                    🏗 {ch.name}
                  </Link>
                  <p className="text-xs text-neutral-500">
                    {[ch.address, ch.postalCode, ch.city].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="mt-1 text-xs text-neutral-400">
                    {ch.customerCount} {t("chantiers.customersWord")}
                    {(ch.startDate || ch.endDate) &&
                      ` · ${ch.startDate ?? "…"} → ${ch.endDate ?? "…"}`}
                    {!ch.active && ` · ${t("chantiers.inactiveWord")}`}
                  </p>
                </div>
                <span className="flex shrink-0 gap-1.5">
                  <Link
                    href={`/chantiers/${ch.id}`}
                    className="rounded-md border border-neutral-700 px-3 py-1 text-xs hover:border-lime"
                  >
                    {t("chantiers.openBtn")}
                  </Link>
                  {canEdit && (
                    <button
                      onClick={() => setEditing(ch)}
                      className="rounded-md border border-neutral-700 px-3 py-1 text-xs hover:border-lime"
                    >
                      {t("chantiers.editBtn")}
                    </button>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        {editing != null && companyId != null && (
          <ChantierModal
            companyId={companyId}
            chantier={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </main>
    </ErpShell>
  );
}

function ChantierModal({
  companyId,
  chantier,
  onClose,
}: {
  companyId: number;
  chantier: ChantierRow | null;
  onClose: () => void;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [form, setForm] = useState({
    name: chantier?.name ?? "",
    city: chantier?.city ?? "",
    address: chantier?.address ?? "",
    postalCode: chantier?.postalCode ?? "",
    notes: chantier?.notes ?? "",
    active: chantier?.active ?? true,
    startDate: chantier?.startDate ?? "",
    endDate: chantier?.endDate ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const done = () => {
    void utils.chantiers.invalidate();
    onClose();
  };
  const createMutation = trpc.chantiers.create.useMutation({
    onSuccess: done,
    onError: (e) => setError(e.message),
  });
  const updateMutation = trpc.chantiers.update.useMutation({
    onSuccess: done,
    onError: (e) => setError(e.message),
  });
  const pending = createMutation.isPending || updateMutation.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload = {
      name: form.name.trim(),
      city: form.city.trim() || undefined,
      address: form.address.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      notes: form.notes.trim() || undefined,
      active: form.active,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };
    if (chantier == null) createMutation.mutate({ companyId, ...payload });
    else updateMutation.mutate({ companyId, chantierId: chantier.id, patch: payload });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg space-y-3 rounded-lg border border-neutral-800 bg-neutral-950 p-5"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {chantier == null ? t("chantiers.newBtn") : t("chantiers.editTitle")}
          </h2>
          <button type="button" onClick={onClose} className="text-lg text-neutral-500">
            ×
          </button>
        </div>
        {error && <p className="rounded bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
        <label className="block text-xs uppercase tracking-wider text-neutral-500">
          {t("chantiers.fieldName")}
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={200}
            className={inputClass + " mt-1"}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("chantiers.fieldCity")}
            <input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              maxLength={120}
              className={inputClass + " mt-1"}
            />
          </label>
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("chantiers.fieldPostal")}
            <input
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
              maxLength={20}
              className={inputClass + " mt-1"}
            />
          </label>
        </div>
        <label className="block text-xs uppercase tracking-wider text-neutral-500">
          {t("chantiers.fieldAddress")}
          <input
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            maxLength={300}
            className={inputClass + " mt-1"}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("chantiers.fieldStart")}
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              className={inputClass + " mt-1"}
            />
          </label>
          <label className="block text-xs uppercase tracking-wider text-neutral-500">
            {t("chantiers.fieldEnd")}
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              className={inputClass + " mt-1"}
            />
          </label>
        </div>
        <label className="block text-xs uppercase tracking-wider text-neutral-500">
          {t("chantiers.fieldNotes")}
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            maxLength={2000}
            className={inputClass + " mt-1 resize-y"}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          {t("chantiers.fieldActive")}
        </label>
        {chantier != null && <ManagerSection companyId={companyId} chantierId={chantier.id} />}
        <button
          type="submit"
          disabled={pending || form.name.trim() === ""}
          className="w-full rounded-md bg-lime px-4 py-2.5 text-sm font-semibold text-on-lime disabled:opacity-40"
        >
          {pending ? "…" : t("chantiers.saveBtn")}
        </button>
      </form>
    </div>
  );
}

/**
 * F-chantier-manager-comm — responsável do chantier: conta (papel gerente)
 * + % sobre débosselage por tipo de cliente (pro/particular). Base = devis
 * HT pós-franquia; sai da parte da casa.
 */
function ManagerSection({ companyId, chantierId }: { companyId: number; chantierId: number }) {
  const t = useT();
  const utils = trpc.useUtils();
  const listQ = trpc.chantiers.listManagers.useQuery({ companyId, chantierId });
  const candQ = trpc.chantiers.managerCandidates.useQuery({ companyId });
  const [userId, setUserId] = useState<number | "">("");
  const [pctPro, setPctPro] = useState("");
  const [pctPart, setPctPart] = useState("");
  const upsertMut = trpc.chantiers.upsertManager.useMutation({
    onSuccess: () => {
      void utils.chantiers.listManagers.invalidate();
      setUserId("");
      setPctPro("");
      setPctPart("");
    },
  });
  const removeMut = trpc.chantiers.removeManager.useMutation({
    onSuccess: () => void utils.chantiers.listManagers.invalidate(),
  });
  const rows = listQ.data?.rows ?? [];
  return (
    <div className="space-y-2 rounded-md border border-neutral-800 p-3">
      <p className="text-xs uppercase tracking-wider text-neutral-500">
        👷 {t("chantiers.managerTitle")}
      </p>
      <p className="text-[11px] text-neutral-500">{t("chantiers.managerHint")}</p>
      {rows.map((r) => (
        <div
          key={r.id}
          className="flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-sm"
        >
          <span className="min-w-0 truncate">
            {r.userName}
            <span className="ml-2 font-mono text-xs text-neutral-400">
              {t("chantiers.managerProShort")} {Number(r.commissionPctPro)}% ·{" "}
              {t("chantiers.managerPartShort")} {Number(r.commissionPctParticular)}%
            </span>
          </span>
          <button
            type="button"
            onClick={() => removeMut.mutate({ companyId, managerId: r.id })}
            className="shrink-0 text-xs text-red-400 hover:underline"
          >
            {t("chantiers.managerRemove")}
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2">
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value === "" ? "" : Number(e.target.value))}
          className={inputClass + " min-w-40 flex-1"}
        >
          <option value="">{t("chantiers.managerPick")}</option>
          {(candQ.data?.rows ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({u.email})
            </option>
          ))}
        </select>
        <label className="block text-[10px] uppercase tracking-wider text-neutral-500">
          {t("chantiers.managerProShort")} %
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={pctPro}
            onChange={(e) => setPctPro(e.target.value)}
            className={inputClass + " mt-0.5 w-20"}
          />
        </label>
        <label className="block text-[10px] uppercase tracking-wider text-neutral-500">
          {t("chantiers.managerPartShort")} %
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={pctPart}
            onChange={(e) => setPctPart(e.target.value)}
            className={inputClass + " mt-0.5 w-20"}
          />
        </label>
        <button
          type="button"
          disabled={upsertMut.isPending || userId === "" || pctPro === "" || pctPart === ""}
          onClick={() =>
            userId !== "" &&
            upsertMut.mutate({
              companyId,
              chantierId,
              userId,
              commissionPctPro: Number(pctPro),
              commissionPctParticular: Number(pctPart),
            })
          }
          className="rounded-md bg-lime px-3 py-2 text-xs font-semibold text-on-lime disabled:opacity-40"
        >
          {upsertMut.isPending ? "…" : t("chantiers.managerAdd")}
        </button>
      </div>
      {upsertMut.error && <p className="text-xs text-red-400">{upsertMut.error.message}</p>}
      {(candQ.data?.rows ?? []).length === 0 && !candQ.isLoading && (
        <p className="text-[11px] text-amber-300">{t("chantiers.managerNoCandidates")}</p>
      )}
    </div>
  );
}
